import { useLazyQuery, useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/material';
import AutocompleteWithFetch from 'components/Inputs/AutoCompletewithFetch';
import TextField from 'components/Inputs/TextField';
import {
  UPDATE_USER_MUTATION,
  UpdateUserResponse,
  UpdateUserVariables,
} from 'graphql/mutation/user';

import {
  GET_BANDS_QUERY,
  GET_DEPARTMENTS_QUERY,
  GET_DESIGNATIONS_QUERY,
  GET_HODs_QUERY,
  GET_LEVELS_QUERY,
  GET_ROLES_QUERY,
  GET_USERS_FOR_AC,
  GetBandsQueryResponse,
  GetBandsQueryVariables,
  GetHODsResponse,
  GetLevelsQueryResponse,
  GetLevelsQueryVariables,
  GetRolesResponse,
  GetUserDepartmentResponse,
  GetUserDepartmentVariables,
  GetUserDesignationsResponse,
  GetUserDesignationsVariables,
  GetUsersQueryResponse,
  GetUsersQueryVariables,
} from 'graphql/query/user';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { User } from 'types';

const convertStringToAutocompleteType = (val: string) => {
  return {
    _id: val,
    name: val,
  };
};

const UpdateEmployeeForm: React.FC<{ initialData: User }> = ({ initialData }) => {
  const [formstate, setFormstate] = useState<Record<string, any>>({
    empId: initialData.empId,
    email: initialData.email,
    name: initialData.name,
    location: initialData.location,
    department: convertStringToAutocompleteType(initialData.department),
    role: initialData.role,
    designation: convertStringToAutocompleteType(initialData.designation),
    band: convertStringToAutocompleteType(initialData.band),
    level: initialData.level,
    hod: initialData.hod,
    reportingManager: initialData.reportingManager,
  });

  const [error, setError] = useState('');

  const [getUserDepartment, { data: departments, loading: loadingDepartments }] = useLazyQuery<
    GetUserDepartmentResponse,
    GetUserDepartmentVariables
  >(GET_DEPARTMENTS_QUERY);

  const [updateUser, { loading: updatingUser }] = useMutation<
    UpdateUserResponse,
    UpdateUserVariables
  >(UPDATE_USER_MUTATION);

  const [getUsers, { data: users, loading: loadingUsers }] = useLazyQuery<
    GetUsersQueryResponse,
    GetUsersQueryVariables
  >(GET_USERS_FOR_AC);

  const [getBands, { data: bands, loading: loadingBands }] = useLazyQuery<
    GetBandsQueryResponse,
    GetBandsQueryVariables
  >(GET_BANDS_QUERY);

  const [getLevels, { data: levels, loading: loadingLevels }] = useLazyQuery<
    GetLevelsQueryResponse,
    GetLevelsQueryVariables
  >(GET_LEVELS_QUERY);

  const [getUserDesignations, { data: designations, loading: loadingDesignations }] = useLazyQuery<
    GetUserDesignationsResponse,
    GetUserDesignationsVariables
  >(GET_DESIGNATIONS_QUERY);

  const [getHODs, { data: HODs, loading: loadingHODs }] =
    useLazyQuery<GetHODsResponse>(GET_HODs_QUERY);

  const [getRoles, { data: roles, loading: loadingRoles }] =
    useLazyQuery<GetRolesResponse>(GET_ROLES_QUERY);

  const handleChange = (fieldName: string, value: any) => {
    setFormstate(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleDesignationChange = (val: any) => {
    const obj = {
      designation: val,
      band: '',
      level: '',
    };
    setFormstate(prev => ({
      ...prev,
      ...obj,
    }));
  };

  const handleBandChange = (val: any) => {
    setFormstate(prev => ({
      ...prev,
      band: val,
    }));

    if (!!val._id) {
      getLevels({
        variables: {
          band: val._id,
          designation: formstate.designation?._id,
        },
      });
    }
  };

  const handleSubmit = () => {
    const refinedData = {
      userId: initialData._id,
      location: formstate.location,
      role: formstate.role._id,
      department: formstate.department._id,
      designation: formstate.designation._id,
      band: formstate.band._id,
      level: !!levels?.getLevels?.length ? levels.getLevels?.[0] : initialData.level,
      reportingManager: formstate.reportingManager._id,
      hod: formstate.hod._id,
    };

    updateUser({
      variables: {
        input: refinedData,
      },
      onCompleted: () => {
        setFormstate({});
        toast.success('Employee Updated Successfully');
      },
    });
  };

  return (
    <Grid item xs={12}>
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Grid container columnSpacing={2} rowGap={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Emp Id"
              name="empId"
              value={formstate.empId ?? ''}
              onChange={e => handleChange('empId', e.target.value)}
              required
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              name="email"
              value={formstate.email ?? ''}
              onChange={e => handleChange('email', e.target.value)}
              type="email"
              required
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Name"
              name="name"
              value={formstate.name ?? ''}
              onChange={e => handleChange('name', e.target.value)}
              required
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Location"
              value={formstate.location ?? ''}
              onChange={e => handleChange('location', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getRoles}
              label="Role"
              loading={loadingRoles}
              options={roles?.getRoles ?? []}
              value={formstate.role ?? { _id: '', name: '' }}
              handleChange={val => handleChange('role', val)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getUserDepartment}
              label="Department"
              loading={loadingDepartments}
              value={formstate.department ?? { _id: '', name: '' }}
              handleChange={val => handleChange('department', val)}
              options={departments?.getUserDepartment ?? []}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getUserDesignations}
              loading={loadingDesignations}
              options={designations?.getDesignations ?? []}
              label="Designation"
              value={formstate.designation ?? ''}
              handleChange={val => handleDesignationChange(val)}
              required
              disabled={!!!formstate.department}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getBands}
              loading={loadingBands}
              options={bands?.getBands ?? []}
              label="Band"
              value={formstate.band ?? ''}
              handleChange={val => handleBandChange(val)}
              required
              disabled={!!!formstate.designation}
              variables={{
                designation:
                  typeof formstate.designation === 'string'
                    ? formstate.designation
                    : formstate.designation?._id,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              loading={loadingLevels}
              label="Level"
              value={
                !!formstate.band?._id
                  ? !!levels?.getLevels.length
                    ? levels.getLevels[0]
                    : loadingLevels
                      ? ''
                      : initialData.level
                  : ''
              }
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getHODs}
              label="HOD"
              loading={loadingHODs}
              value={formstate.hod ?? { _id: '', name: '' }}
              options={HODs?.getHODs ?? []}
              handleChange={val => handleChange('hod', val)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getUsers}
              label="Reporting Manager"
              handleChange={val => handleChange('reportingManager', val)}
              loading={loadingUsers}
              options={users?.getUsers ?? []}
              value={formstate.reportingManager ?? { _id: '', name: '' }}
              required
            />
          </Grid>

          <Grid item container justifyContent={'flex-end'} xs={12}>
            <LoadingButton
              variant="contained"
              type="submit"
              disabled={!!error}
              loading={updatingUser}
            >
              Update
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default UpdateEmployeeForm;
